/**
 * Shared drag-and-drop id conventions for the List page's single multi-container
 * DnD context. Section cards register both a sortable (raw `sectionId`) and an
 * item drop-zone (`container:<sectionId>`); the Unsorted bucket uses a reserved
 * key. Keeping the scheme in one place lets `ListDetailPage` (which resolves drop
 * targets) and `SectionCard` (which creates the drop-zones) stay in sync.
 */

import { arrayMove } from '@dnd-kit/sortable';
import { closestCenter, closestCorners, type CollisionDetection } from '@dnd-kit/core';

export const UNSORTED_KEY = '__unsorted__';
const CONTAINER_PREFIX = 'container:';

/** Droppable id for a section's item list (null = the Unsorted bucket). */
export function itemContainerId(sectionId: string | null): string {
  return CONTAINER_PREFIX + (sectionId ?? UNSORTED_KEY);
}

/**
 * If `id` is an item drop-zone, returns its section id (null = Unsorted);
 * otherwise `isContainer` is false (the id is an item or section sortable).
 */
export function parseItemContainerId(id: string): {
  isContainer: boolean;
  sectionId: string | null;
} {
  if (id.startsWith(CONTAINER_PREFIX)) {
    const key = id.slice(CONTAINER_PREFIX.length);
    return { isContainer: true, sectionId: key === UNSORTED_KEY ? null : key };
  }
  return { isContainer: false, sectionId: null };
}

/** Minimal item shape the drop resolver needs. */
export interface DraggableItem {
  itemId: string;
  sectionId: string | null;
  sortOrder: number;
}

export type ItemDropResult =
  | { kind: 'reorder'; order: string[] }
  | { kind: 'move'; itemId: string; destSection: string | null; order: string[] }
  | null;

/**
 * Resolve an item drop in the List page's single DnD context into a persistence
 * action. Pure + framework-free so it's unit-testable (dnd-kit drag itself isn't
 * simulatable in jsdom).
 *
 * - `overId` is an item drop-zone (`container:<sectionId>`) or a sibling item id.
 * - Same source & destination section → `reorder` (new order for that section).
 * - Different section → `move` (destination's new ordered ids incl. the item) —
 *   returns `null` for a non-manager (cross-section is a manager/owner action).
 */
export function resolveItemDrop(params: {
  activeId: string;
  overId: string;
  items: DraggableItem[];
  isManager: boolean;
}): ItemDropResult {
  const { activeId, overId, items, isManager } = params;
  const moved = items.find((i) => i.itemId === activeId);
  if (!moved) return null;
  const sourceSection = moved.sectionId ?? null;

  const parsed = parseItemContainerId(overId);
  let destSection: string | null;
  if (parsed.isContainer) {
    destSection = parsed.sectionId;
  } else {
    const overItem = items.find((i) => i.itemId === overId);
    if (!overItem) return null;
    destSection = overItem.sectionId ?? null;
  }

  const destIds = items
    .filter((i) => (i.sectionId ?? null) === destSection)
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((i) => i.itemId);

  if (destSection === sourceSection) {
    const oldIndex = destIds.indexOf(activeId);
    const newIndex = parsed.isContainer ? destIds.length - 1 : destIds.indexOf(overId);
    if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return null;
    return { kind: 'reorder', order: arrayMove(destIds, oldIndex, newIndex) };
  }

  if (!isManager) return null; // cross-section is a manager/owner capability
  const rest = destIds.filter((id) => id !== activeId);
  const insertAt = parsed.isContainer ? rest.length : Math.max(0, rest.indexOf(overId));
  return {
    kind: 'move',
    itemId: activeId,
    destSection,
    order: [...rest.slice(0, insertAt), activeId, ...rest.slice(insertAt)],
  };
}

/**
 * The multi-select analogue of arrayMove: reorder `ids` by lifting the SELECTED
 * subset (kept in their existing relative order) and re-inserting them as one
 * contiguous block at the drop position. `activeId` is the card actually dragged;
 * `overId` is the item being dropped on, or `null` for a container / end drop
 * (block goes to the end). When dropping on an unselected item, the block lands
 * before or after it depending on the drag direction (matching single-item
 * arrayMove); dropping on a selected item inserts at that item's slot among the
 * unselected. Shared by the List page and the My Tasks board.
 */
export function reorderSelectedIds(
  ids: string[],
  selected: Set<string>,
  activeId: string,
  overId: string | null,
): string[] {
  const selectedBlock = ids.filter((id) => selected.has(id));
  if (selectedBlock.length === 0) return ids;
  const rest = ids.filter((id) => !selected.has(id));
  if (overId == null) return [...rest, ...selectedBlock];
  const overInRest = rest.indexOf(overId);
  let insertAt: number;
  if (overInRest === -1) {
    // Dropped on a selected item — insert where it sits among the unselected.
    const overIndex = ids.indexOf(overId);
    insertAt =
      overIndex === -1
        ? rest.length
        : ids.slice(0, overIndex).filter((id) => !selected.has(id)).length;
  } else {
    // Dropped on an unselected item — after it when dragging down, before when up.
    const activeIndex = ids.indexOf(activeId);
    const overIndex = ids.indexOf(overId);
    insertAt = activeIndex !== -1 && activeIndex < overIndex ? overInRest + 1 : overInRest;
  }
  return [...rest.slice(0, insertAt), ...selectedBlock, ...rest.slice(insertAt)];
}

/**
 * The two-mode collision strategy shared by every todo drag surface (the shared
 * List page and the personal "My Tasks" board):
 *
 * - A SECTION drag only targets its own siblings (a clean reorder within its
 *   level), resolved by `closestCenter`.
 * - An ITEM/ENTRY drag EXCLUDES the section-card sortable droppables so task
 *   sorting behaves like a plain multi-container board, resolved by
 *   `closestCorners`.
 *
 * Callers differ only in how they derive an active section's siblings and the
 * full set of section sortable ids, so both are injected.
 */
export function makeTodoCollisionDetection(params: {
  /** The sibling section sortable ids for the given active section id. */
  siblingSectionIdsOf: (activeSectionId: string) => Iterable<string>;
  /** Every section sortable id (excluded from item-drag collision). */
  allSectionIds: Set<string>;
}): CollisionDetection {
  return (args) => {
    if (args.active.data.current?.type === 'section') {
      const sibs = new Set(params.siblingSectionIdsOf(String(args.active.id)));
      return closestCenter({
        ...args,
        droppableContainers: args.droppableContainers.filter((d) => sibs.has(String(d.id))),
      });
    }
    return closestCorners({
      ...args,
      droppableContainers: args.droppableContainers.filter(
        (d) => !params.allSectionIds.has(String(d.id)),
      ),
    });
  };
}
